import { Trans, t } from "@lingui/macro";
import { Plus, Trash2, MoreHorizontal, FileText, Database, Import } from "lucide-react";
import { useEffect, useState } from "react";
import { BotPromptSuggestion } from "../../models/BotPromptSuggestion";
import { Soribot } from "../../models/Soribot";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import { useHistory } from "../../hooks/useRouterCompat";
import { My } from "../../services/My";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Loader2 } from "lucide-react";

const SoribotSuggestions: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [showImport, setShowImport] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [suggestions, setSuggestions] = useState<BotPromptSuggestion[]>([]);
  const [selectedSug, setSelectedSug] = useState<BotPromptSuggestion>();
  const { toast } = useToast();
  const history = useHistory();
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });

  useEffect(() => {
    setSuggestions([]);
    setInProgress(true);
    setSelectedSug(undefined);
    return Fire.bot.subscribeToPromptSuggestions(bot.id, sugs => {
      setSuggestions(prev => {
        // Should update state correctly if needed, but Fire subs returns new arrays usually.
        return sugs;
      });
      setInProgress(false);
      if (!selectedSug) {
        setSelectedSug(sugs[0]);
      }
    });
  }, [bot])

  // Need to sync selectedSug with updates from suggestions list if we edit it in place
  useEffect(() => {
    if (selectedSug) {
      const fresh = suggestions.find(s => s.id === selectedSug.id);
      if (fresh) setSelectedSug(fresh);
    } else if (suggestions.length > 0 && !selectedSug) {
      setSelectedSug(suggestions[0]);
    }
  }, [suggestions]);


  return <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
      <div className="lg:col-span-1 border rounded flex flex-col bg-white">
        <div className="p-3 border-b flex items-center justify-between bg-gray-50">
          <h5 className="font-semibold text-sm"><Trans>Suggestions</Trans> ({suggestions.length})</h5>
          <div className="flex items-center gap-1">
            {showImport && <SuggestionImportModal botId={bot.id} open={showImport} onOpenChange={setShowImport} onDone={saveSugsList} />}
            <SuggestionsMoreDropdown onImport={() => setShowImport(true)} onCreate={addSug} onDeleteAll={deleteAll} onSaveAsKnowledge={saveAsKnowledge} />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-gray-500" /></div>}
          {suggestions.length === 0 && !inProgress && <div className="p-4 text-center text-gray-500 text-sm"><Trans>Add with Import or Create.</Trans></div>}
          <div className="divide-y">
            {suggestions.map(s => <SuggestionMenuItem key={s.id} sug={s} />)}
          </div>
        </ScrollArea>
      </div>

      <div className="lg:col-span-2 border rounded bg-white p-4 overflow-y-auto">
        {selectedSug ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h5 className="font-semibold">
                {selectedSug.updated ? <Trans>Edit Suggestion</Trans> : <Trans>Create new</Trans>}
              </h5>
              <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteRun(selectedSug)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label><Trans>Question</Trans></Label>
                <Textarea
                  value={selectedSug.prompt}
                  onChange={e => {
                    // Mutating state directly is bad practice in React, ideally should use setSuggestions or a local draft state
                    // For compatibility with previous code structure, better to update a local copy or force re-render
                    // But let's try to update state properly for React
                    // Here we force update the object itself to match legacy behavior, but trigger re-render
                    const newSug = Object.assign(Object.create(Object.getPrototypeOf(selectedSug)), selectedSug);
                    newSug.prompt = e.target.value;
                    setSelectedSug(newSug);
                  }}
                  placeholder={t`Enter a prompt`}
                />
              </div>

              <div className="space-y-2">
                <Label><Trans>Fixed answer</Trans></Label>
                <Textarea
                  value={selectedSug.response || ''}
                  onChange={e => {
                    const newSug = Object.assign(Object.create(Object.getPrototypeOf(selectedSug)), selectedSug);
                    newSug.response = e.target.value;
                    setSelectedSug(newSug);
                  }}
                  className="min-h-[150px]"
                  placeholder={t`Enter a fixed response or leave it empty for a realtime response.`}
                />
                <p className="text-xs text-gray-500"><Trans>Questions with empty answer will be asked as a normal question.</Trans></p>
              </div>

              <div className="flex items-center justify-between py-2 border-t">
                <div className="text-xs text-gray-500">
                  {selectedSug.updated && <Trans>Updated {UtilApp.dateStrToTimeAndDay(selectedSug.updated, My.user.lang)}</Trans>}
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={selectedSug.isActive}
                    onCheckedChange={(checked) => {
                      const newSug = Object.assign(Object.create(Object.getPrototypeOf(selectedSug)), selectedSug);
                      newSug.isActive = !!checked;
                      setSelectedSug(newSug);
                    }}
                  />
                  <Label htmlFor="active"><Trans>Active</Trans></Label>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => saveSug(selectedSug)}><Trans>Save changes</Trans></Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <Trans>Select a suggestion to edit</Trans>
          </div>
        )}
      </div>
    </div>

    <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alert.title}</AlertDialogTitle>
          <AlertDialogDescription>{alert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
          <AlertDialogAction onClick={() => { alert.confirm?.(); setAlert(prev => ({ ...prev, open: false })) }}><Trans>Confirm</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>

  async function saveSug(sug: BotPromptSuggestion) {
    if (!sug.prompt) return;
    await Fire.bot.saveSuggestion(sug)
    toast({ title: t`Suggestion saved` });
  }

  async function saveSugsList(sugs: BotPromptSuggestion[]) {
    setShowImport(false);
    setInProgress(true);
    await Fire.bot.saveSuggestions(sugs);
    setInProgress(false);
    toast({ title: t`Suggestions imported` });
  }

  function addSug() {
    // Create new suggestion and select it
    const newSug = new BotPromptSuggestion(UUID.get(), bot.id, '', null);
    // Ideally we should add it to list, but subs will handle it if we save it?
    // Or we just set it as selected draft. Legacy code just set it as selected.
    setSelectedSug(newSug);
  }

  function deleteRun(sug: BotPromptSuggestion) {
    Fire.bot.deleteSuggestion(sug)
      .then(() => {
        toast({ title: t`Suggestion deleted` });
        setSelectedSug(undefined);
      });
  }

  function SuggestionMenuItem({ sug }: { sug: BotPromptSuggestion }) {
    const isSelected = selectedSug?.id === sug.id;
    return <div
      className={`p-3 cursor-pointer text-sm hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
      onClick={() => setSelectedSug(sug)}
    >
      <div className={`truncate font-medium ${sug.isActive ? '' : 'text-gray-400'}`}>{sug.prompt || t`New suggestion`}</div>
      {!sug.isActive && <div className="text-xs text-gray-400 mt-1"><Trans>Inactive</Trans></div>}
    </div>
  }

  function deleteAll() {
    setAlert({
      open: true,
      title: t`Delete all suggestions`,
      message: t`Are you sure you want to delete all suggestions?`,
      confirm: () => {
        Fire.bot.deleteAllSuggestions(bot.id)
          .then(() => {
            toast({ title: t`All suggestions deleted` });
            setSelectedSug(undefined);
          });
      }
    });
  }

  async function saveAsKnowledge() {
    setAlert({
      open: true,
      title: t`Save as knowledge`,
      message: t`Do you want to save all suggestions as a knowledge document? This will overwrite the prevously saved document based on suggestions.`,
      confirm: () => {
        setInProgress(true);
        Fire.bot.saveSuggestionsAsKnowledge(bot.id)
          .then(() => {
            toast({ title: t`Suggestions saved as knowledge` });
            history.replace(`/soribot/datasources/${bot.id}`)
          }).catch(e => {
            toast({ variant: "destructive", title: `ERROR: ${e.message}` });
          }).finally(() => {
            setInProgress(false);
          });
      }
    });
  }
}

export default SoribotSuggestions;

const SuggestionImportModal: React.FC<{ botId: string, open: boolean, onOpenChange: (open: boolean) => void, onDone: (pairs: BotPromptSuggestion[]) => void }> = ({ botId, open, onOpenChange, onDone }) => {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<BotPromptSuggestion[]>([]);

  function onOpen(isOpen: boolean) {
    if (isOpen) {
      setText('');
      setSuggestions([]);
    }
    onOpenChange(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Import suggestions</Trans></DialogTitle>
          <DialogDescription><Trans>Paste text to import</Trans></DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0 py-4">
          {suggestions.length === 0 ? (
            <Textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={t`Paste exported text from Test cases or Test runs.`}
              className="flex-1 resize-none h-full"
            />
          ) : (
            <div className="flex-1 overflow-auto border rounded divide-y">
              <div className="bg-gray-100 p-2 font-bold text-sm sticky top-0"><Trans>Nr of suggestions: {suggestions.length}</Trans></div>
              {suggestions.map((q, i) => (
                <div key={i} className="p-3 text-sm">
                  <div className="font-semibold mb-1">Q: {q.prompt}</div>
                  <div className="text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap">A: {q.response ?? t`No premade answer`}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}><Trans>Cancel</Trans></Button>
          {suggestions.length === 0 ? (
            <Button disabled={!text} onClick={makeQuestions}><Trans>Next</Trans></Button>
          ) : (
            <>
              <Button variant="outline" onClick={startOver}><Trans>Start over</Trans></Button>
              <Button onClick={finish}><Trans>Finish</Trans></Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  function makeQuestions() {
    let sugs: BotPromptSuggestion[] = [];
    if (text.includes('Q:')) {
      text.split('Q:').forEach((q, i) => {
        if (i !== 0) {
          const index = q.indexOf('A:');
          let answer = null;
          if (index !== -1) {
            answer = q.substring(index + 2)?.trim();
          }
          sugs.push(new BotPromptSuggestion(UUID.get(), botId, q.substring(0, index)?.trim(), answer));
        }
      });
    } else {
      text.split('\n').forEach((q, i) => {
        if (q.trim()) {
          sugs.push(new BotPromptSuggestion(UUID.get(), botId, q.trim(), null));
        }
      });
    }
    setSuggestions(sugs);
  }

  function startOver() {
    setSuggestions([]);
  }

  function finish() {
    onDone(suggestions);
  }
}

const SuggestionsMoreDropdown: React.FC<{ onImport: () => void, onCreate: () => void, onDeleteAll: () => void, onSaveAsKnowledge: () => void }> = ({ onImport, onCreate, onDeleteAll, onSaveAsKnowledge }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm"><Trans>Options</Trans> <MoreHorizontal className="w-4 h-4 ml-1" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onImport}><Import className="w-4 h-4 mr-2" /> <Trans>Import from clipboard</Trans></DropdownMenuItem>
        <DropdownMenuItem onClick={onCreate}><Plus className="w-4 h-4 mr-2" /> <Trans>Create new</Trans></DropdownMenuItem>
        <DropdownMenuItem onClick={onSaveAsKnowledge}><Database className="w-4 h-4 mr-2" /> <Trans>Save as knowledge</Trans></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDeleteAll} className="text-red-600 focus:text-red-700 focus:bg-red-50"><Trash2 className="w-4 h-4 mr-2" /> <Trans>Delete all</Trans></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}